import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson, assetUrl } from "../utils/api";
import { formatCityLabel } from "../utils/resolveRegion";
import useSeo from "../hooks/useSeo";
import useUserMaritalStatus from "../hooks/useUserMaritalStatus";

function scoreTone(score) {
  if (score >= 80) return { bar: "bg-emerald-500", text: "text-emerald-600", label: "高度契合" };
  if (score >= 60) return { bar: "bg-rose-500", text: "text-rose-600", label: "值得深入" };
  if (score >= 40) return { bar: "bg-amber-500", text: "text-amber-600", label: "需多了解" };
  return { bar: "bg-gray-400", text: "text-gray-500", label: "契合偏低" };
}

function DimensionBar({ label, value }) {
  const n = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = scoreTone(n);
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-gray-600">{label}</span>
        <span className={`font-semibold ${tone.text}`}>{n}</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${tone.bar}`}
          style={{ width: `${n}%` }}
        />
      </div>
    </div>
  );
}

function conflictLabel(level) {
  if (level === "high") return { text: "冲突风险偏高", cls: "text-amber-700 bg-amber-50" };
  if (level === "low") return { text: "冲突风险较低", cls: "text-emerald-700 bg-emerald-50" };
  return { text: "冲突风险中等", cls: "text-rose-700 bg-rose-50" };
}

function MatchReportPanel({ match, targetUser }) {
  if (!match) return null;
  const report = match.report || {};
  const score = match.match_score ?? report.match_score ?? 0;
  const longTerm = match.long_term_index ?? report.long_term_index ?? score;
  const conflict = match.conflict_risk || report.conflict_risk || "medium";
  const conflictTone = conflictLabel(conflict);
  const advantages = match.advantages || report.advantages || [];
  const risks = match.risks || report.risks || [];
  const dims = report.dimension_scores || {};
  const tone = scoreTone(score);
  void longTerm;
  const preferredOrder = [
    "personality",
    "communication_style",
    "values",
    "family_view",
    "marriage_view",
    "lifestyle",
    "relationship_style",
    "emotion_style",
    "radar",
  ];
  const dimEntries = preferredOrder
    .filter((k) => dims[k] != null)
    .map((k) => [k, dims[k]])
    .concat(
      Object.entries(dims).filter(([k]) => !preferredOrder.includes(k))
    );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-rose-100 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <img
            src={assetUrl(targetUser?.avatar) || "https://picsum.photos/80"}
            alt=""
            className="w-16 h-16 rounded-full object-cover border"
          />
          <div>
            <h2 className="text-xl font-bold text-gray-900">AI 匹配报告</h2>
            <p className="text-sm text-gray-500">
              与 {targetUser?.name || "对方"}
              {targetUser?.age ? ` · ${targetUser.age}岁` : ""}
              {(() => {
                const loc = formatCityLabel(
                  targetUser?.province,
                  targetUser?.city,
                  targetUser?.district,
                  ""
                );
                return loc ? ` · ${loc}` : "";
              })()}
            </p>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="text-center min-w-[88px]">
            <div className={`text-lg font-semibold ${tone.text}`}>{tone.label}</div>
            <div className="text-xs text-gray-400 mt-0.5">关系观察</div>
          </div>
          <div className="text-center min-w-[88px]">
            <div className={`text-sm font-medium ${conflictTone.cls} px-2 py-1 rounded-full`}>
              {conflictTone.text}
            </div>
            <div className="text-xs text-gray-400 mt-1">沟通风险</div>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        这不是「适不适合」的分数结论，而是帮助你理解双方：哪里相近、哪里可能分歧。
      </p>

      {report.personality_note && (
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-xs px-2.5 py-1 rounded-full bg-gray-50 text-gray-600">
            {report.personality_note}
          </span>
        </div>
      )}

      {report.summary_text && (
        <p className="text-sm text-gray-700 bg-rose-50 rounded-xl p-4 mb-5 leading-relaxed">
          {report.summary_text}
        </p>
      )}

      {dimEntries.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">双方对照维度</h3>
          <div className="space-y-3">
            {dimEntries.map(([key, val]) => (
              <DimensionBar key={key} label={dimLabel(key)} value={val} />
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div className="bg-emerald-50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-emerald-800 mb-2">共同点 / 可能吸引</h3>
          {advantages.length ? (
            <ul className="space-y-1.5 text-sm text-emerald-900">
              {advantages.map((a) => (
                <li key={a} className="flex gap-2">
                  <span className="shrink-0">✓</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-emerald-800/70">暂未识别到明显共同点</p>
          )}
        </div>
        <div className="bg-amber-50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-amber-800 mb-2">不同点 / 可能冲突</h3>
          {risks.length ? (
            <ul className="space-y-1.5 text-sm text-amber-900">
              {risks.map((r) => (
                <li key={r} className="flex gap-2">
                  <span className="shrink-0">!</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-amber-800/70">暂未识别到明显分歧</p>
          )}
        </div>
      </div>

      <div className="bg-sky-50 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-sky-800 mb-2">AI 建议</h3>
        <p className="text-sm text-sky-950 leading-relaxed">
          {match.recommendation ||
            report.recommendation ||
            "如果开始认识，可以先从共同兴趣和生活方式聊起，不必一开始就讨论过于沉重的话题。"}
        </p>
      </div>

      {targetUser?.id && (
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to={`/user/${targetUser.id}`}
            className="px-4 py-2 rounded-full bg-rose-500 text-white text-sm hover:bg-rose-600"
          >
            返回资料 · 加好友
          </Link>
          <Link
            to="/discover"
            className="px-4 py-2 rounded-full border border-rose-300 text-rose-600 text-sm hover:bg-rose-50"
          >
            继续浏览其他人
          </Link>
          <Link
            to="/ai/hub/advisor"
            className="px-4 py-2 rounded-full border border-amber-300 text-amber-800 text-sm hover:bg-amber-50"
          >
            学习如何与异性沟通
          </Link>
          <Link
            to={`/ai/simulation?target=${targetUser.id}`}
            className="px-4 py-2 rounded-full border border-gray-200 text-gray-600 text-sm hover:border-rose-300"
          >
            关系模拟
          </Link>
        </div>
      )}
    </div>
  );
}

function dimLabel(key) {
  const map = {
    family_view: "家庭观",
    marriage_view: "婚姻期待",
    lifestyle: "生活方式",
    communication_style: "沟通方式",
    personality: "性格匹配",
    emotion_style: "情绪风格",
    values: "价值观",
    relationship_style: "关系模式",
    radar: "人格雷达相近度",
    goals: "人生目标",
  };
  return map[key] || key;
}

export default function AiMatchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = Number(localStorage.getItem("user_id"));
  const targetFromQuery = searchParams.get("target");
  const { isMarried, loading: maritalLoading } = useUserMaritalStatus();

  const [recommendations, setRecommendations] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [activeMatch, setActiveMatch] = useState(null);
  const [activeTarget, setActiveTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [loadError, setLoadError] = useState("");

  useSeo({
    title: "AI匹配报告 | AI婚恋智能体",
    metaDescription: "基于双方婚恋画像的 AI 深度匹配评分与建议。",
    path: "/ai/match",
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/ai/match")}`, {
        replace: true,
      });
      return;
    }
    if (!maritalLoading && isMarried) {
      navigate("/ai/consultant", { replace: true });
    }
  }, [userId, navigate, isMarried, maritalLoading]);

  useEffect(() => {
    if (!userId || maritalLoading || isMarried) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const { res, data } = await apiJson(
          `/api/ai/match/recommendations?user_id=${userId}&limit=8`
        );
        if (!res.ok) throw new Error(data?.detail || "加载推荐失败");
        if (cancelled) return;
        setRecommendations(data.items || []);
        setMyProfile(data.my_profile || null);

        if (targetFromQuery) {
          await runMatch(Number(targetFromQuery), false);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setLoadError(err.message || "加载失败，请确认后端已重启");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, targetFromQuery, isMarried, maritalLoading]);

  const runMatch = async (targetUserId, showSpinner = true) => {
    if (!targetUserId || analyzing) return;
    if (showSpinner) setAnalyzing(true);
    try {
      const { res, data } = await apiJson("/api/ai/match", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, target_user_id: targetUserId }),
      });
      if (!res.ok) throw new Error(data?.detail || "匹配分析失败");
      setActiveMatch(data.match);
      setActiveTarget(data.target_user);
    } catch (err) {
      alert(err.message || "匹配分析失败");
    } finally {
      if (showSpinner) setAnalyzing(false);
    }
  };

  const progress = myProfile?.interview_progress ?? 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 flex flex-col">
      <Header />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">我和TA的AI关系分析</h1>
            <p className="text-sm text-gray-500 mt-2">
              看看你们为什么互相吸引，又可能在哪些地方产生分歧。重点是理解，不是打分。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/ai/personality"
              className="px-4 py-2 rounded-full border border-rose-300 text-rose-600 text-sm hover:bg-white"
            >
              我的 AI 人格
            </Link>
            <Link
              to="/ai/consultant"
              className="px-4 py-2 rounded-full border border-rose-300 text-rose-600 text-sm hover:bg-white"
            >
              完善我的画像
            </Link>
            <Link
              to="/center/ai-memory"
              className="px-4 py-2 rounded-full border border-gray-200 text-gray-600 text-sm hover:bg-white"
            >
              我的 AI 记忆
            </Link>
          </div>
        </div>

        {loadError && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl px-4 py-3">
            {loadError}
          </div>
        )}

        {myProfile && (
          <div className="bg-white/80 border border-rose-100 rounded-xl px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm mb-2">
              <span className="text-gray-700">我的画像完整度</span>
              <span className="font-semibold text-rose-600">{progress}%</span>
            </div>
            <div className="h-2 rounded-full bg-rose-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-rose-500 transition-all"
                style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
              />
            </div>
            {progress < 80 && (
              <p className="text-xs text-amber-800 mt-2">
                访谈尚未完成。建议先与
                <Link to="/ai/consultant" className="underline mx-1">
                  AI 咨询师
                </Link>
                多聊几轮，匹配会更准。当前结果已用基础资料估算。
              </p>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          <section className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">为你推荐</h2>
              {!loading && recommendations.length > 0 && (
                <span className="text-xs text-gray-400">{recommendations.length} 位</span>
              )}
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white/70 rounded-2xl p-4 border border-gray-100 animate-pulse h-24"
                  />
                ))}
              </div>
            ) : recommendations.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-rose-200 p-6 text-center">
                <p className="font-medium text-gray-700 mb-2">暂无可推荐会员</p>
                <p className="text-sm text-gray-500 mb-4">
                  可以先完善画像，或去发现页看看有缘人。
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Link
                    to="/ai/consultant"
                    className="px-3 py-1.5 rounded-full bg-rose-500 text-white text-sm"
                  >
                    去聊 AI 咨询师
                  </Link>
                  <Link
                    to="/discover"
                    className="px-3 py-1.5 rounded-full border border-rose-300 text-rose-600 text-sm"
                  >
                    寻找对象
                  </Link>
                </div>
              </div>
            ) : (
              recommendations.map((item) => {
                const tone = scoreTone(item.match_score || 0);
                const longTerm = item.long_term_index ?? item.match_score ?? 0;
                const risk = conflictLabel(item.conflict_risk || "medium");
                return (
                  <button
                    key={item.user.id}
                    type="button"
                    onClick={() => runMatch(item.user.id)}
                    className={`w-full text-left bg-white rounded-2xl p-4 border shadow-sm hover:border-rose-300 transition ${
                      activeTarget?.id === item.user.id
                        ? "border-rose-400 ring-2 ring-rose-100"
                        : "border-gray-100"
                    }`}
                  >
                    <div className="flex gap-3">
                      <img
                        src={assetUrl(item.user.avatar) || "https://picsum.photos/64"}
                        alt=""
                        className="w-14 h-14 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <div className="font-semibold text-gray-900 truncate">
                            {item.user.name || "会员"}
                            {item.user.age ? ` · ${item.user.age}` : ""}
                          </div>
                          <div className={`font-bold shrink-0 ${tone.text}`}>
                            {item.match_score}%
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                          {[
                            item.user.personality_type,
                            formatCityLabel(
                              item.user.province,
                              item.user.city,
                              item.user.district,
                              ""
                            ),
                            item.user.job,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "资料完善中"}
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${tone.bar}`}
                            style={{
                              width: `${Math.max(0, Math.min(100, item.match_score || 0))}%`,
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                          <span className="text-rose-600">长期 {longTerm}%</span>
                          {item.personality_similarity != null && (
                            <span className="text-gray-500">
                              性格 {item.personality_similarity}%
                            </span>
                          )}
                          <span className={risk.cls.split(" ")[0]}>{risk.text}</span>
                        </div>
                        <div className="text-xs text-emerald-700 mt-1 truncate">
                          {(item.advantages || []).slice(0, 2).join(" · ") || tone.label}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </section>

          <section className="lg:col-span-3">
            {analyzing && (
              <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
                <span className="inline-block w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
                正在生成正式匹配报告…
              </div>
            )}
            {activeMatch ? (
              <MatchReportPanel match={activeMatch} targetUser={activeTarget} />
            ) : (
              <div className="bg-white rounded-2xl border border-dashed border-rose-200 p-10 text-center text-gray-500 min-h-[280px] flex flex-col items-center justify-center">
                <p className="font-medium text-gray-700 mb-2">选择一位推荐会员</p>
                <p className="text-sm max-w-sm">
                  查看综合匹配度、各维度契合条、优势与风险，以及可执行的相处建议。
                </p>
                {recommendations[0] && (
                  <button
                    type="button"
                    onClick={() => runMatch(recommendations[0].user.id)}
                    className="mt-5 px-4 py-2 rounded-full bg-rose-500 text-white text-sm hover:bg-rose-600"
                  >
                    先看第一位推荐报告
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
